/* eslint-disable camelcase */

import {Mapper} from '@s-ui/domain'

/**
 * @implements Mapper
 * Maps Financing Terms raw data returned by the API to a FinancingConditionEntity
 */
export class FinancingTermsRawDataToFinancingConditionEntityMapper extends Mapper {
  constructor({
    config,
    financingConditionEntityFactory,
    bbvaConditionEntityFactory,
    bmwBankConditionEntityFactory,
    caixaConditionEntityFactory,
    cofidisConditionEntityFactory,
    lendrockConditionEntityFactory,
    mobilizeConditionEntityFactory,
    santanderConditionEntityFactory,
    santanderPriConditionEntityFactory,
    stellantisConditionEntityFactory,
    vwfsConditionEntityFactory,
    confiaConditionEntityFactory,
    validationError
  }) {
    super()
    this._config = config
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._financingConditionEntityFactory = financingConditionEntityFactory
    this._bbvaConditionEntityFactory = bbvaConditionEntityFactory
    this._bmwBankConditionEntityFactory = bmwBankConditionEntityFactory
    this._cofidisConditionEntityFactory = cofidisConditionEntityFactory
    this._caixaConditionEntityFactory = caixaConditionEntityFactory
    this._lendrockConditionEntityFactory = lendrockConditionEntityFactory
    this._mobilizeConditionEntityFactory = mobilizeConditionEntityFactory
    this._santanderConditionEntityFactory = santanderConditionEntityFactory
    this._santanderPriConditionEntityFactory = santanderPriConditionEntityFactory
    this._stellantisConditionEntityFactory = stellantisConditionEntityFactory
    this._vwfsConditionEntityFactory = vwfsConditionEntityFactory
    this._confiaConditionEntityFactory = confiaConditionEntityFactory
    const {AVAILABLE_FINANCING_LENDERS} = this._config.get('search')
    const {
      BBVA,
      BBVA_WITHOUT_INSURANCE,
      BMW_BANK,
      BMW_BANK_MULTI_BRAND,
      CAIXA,
      COFIDIS,
      CONFIA,
      LENDROCK,
      MOBILIZE,
      MOBILIZE_NISSAN,
      MOBILIZE_NISSAN_NSFS,
      SANTANDER,
      SANTANDER_PRI,
      SANTANDER_WITH_CASUALTY_INSURANCE,
      STELLANTIS,
      SANTANDER_WITHOUT_INSURANCE,
      VWFS
    } = config.get('financing').FINANCING_PARTNERS
    const {CASUALTY, NO_INSURANCE, WITHOUT_INSURANCE_BBVA} = config.get('financing').INSURANCE_IDS
    const {BMW_BANK_MULTI_BRAND_FARE_ID, MOBILIZE_NISSAN_NSFS_FARE_ID, MOBILIZE_NISSAN_NSFS_WITH_PROMOTION_FARE_ID} =
      config.get('financing').FARE_ID
    this._availableLenders = AVAILABLE_FINANCING_LENDERS
    this._validationError = validationError
    this._bbva = BBVA
    this._bbvaWithoutInsurance = BBVA_WITHOUT_INSURANCE
    this._bbvaNoInsuranceId = WITHOUT_INSURANCE_BBVA
    this._bmwBank = BMW_BANK
    this._bmwBankMultiBrand = BMW_BANK_MULTI_BRAND
    this._bmwBakMultiBrandFareId = BMW_BANK_MULTI_BRAND_FARE_ID
    this._caixa = CAIXA
    this._cofidis = COFIDIS
    this._lendrock = LENDROCK
    this._mobilize = MOBILIZE
    this._mobilizeNissan = MOBILIZE_NISSAN
    this._mobilizeNissanNsfs = MOBILIZE_NISSAN_NSFS
    this._mobilizeNissanNsfsFareId = MOBILIZE_NISSAN_NSFS_FARE_ID
    this._mobilizeNissanNsfsWithPromotionFareId = MOBILIZE_NISSAN_NSFS_WITH_PROMOTION_FARE_ID
    this._santander = SANTANDER
    this._santanderPri = SANTANDER_PRI
    this._santanderWithCasualtyInsurance = SANTANDER_WITH_CASUALTY_INSURANCE
    this._stellantis = STELLANTIS
    this._casualtyInsuranceId = CASUALTY
    this._santanderWithoutInsurance = SANTANDER_WITHOUT_INSURANCE
    this._noInsuranceId = NO_INSURANCE
    this._vwfs = VWFS
    this._confia = CONFIA
  }

  getLenderLegalTextId = ({fareId, insuranceId, lender}) => {
    switch (`${insuranceId}/${lender}`) {
      case `${this._casualtyInsuranceId}/${this._santander}`:
        return this._santanderWithCasualtyInsurance
      case `${this._noInsuranceId}/${this._santander}`:
        return this._santanderWithoutInsurance
      case `${this._bbvaNoInsuranceId}/${this._bbva}`:
        return this._bbvaWithoutInsurance
      default:
        switch (lender) {
          case this._bmwBank:
            return fareId === this._bmwBakMultiBrandFareId ? this._bmwBankMultiBrand : this._bmwBank
          case this._mobilizeNissan:
            return fareId === this._mobilizeNissanNsfsFareId || fareId === this._mobilizeNissanNsfsFareId
              ? this._mobilizeNissanNsfs
              : this._mobilizeNissan
          case this._bbva:
          case this._santander:
          case this._santanderPri:
          case this._caixa:
          case this._cofidis:
          case this._confia:
          case this._lendrock:
          case this._stellantis:
          case this._vwfs:
          case this._mobilize:
            return lender
          default:
            return null
        }
    }
  }

  getLendersConditions = ({conditionsObj}) => {
    const {lender} = conditionsObj
    switch (lender) {
      case this._bbva:
        return this._bbvaConditionEntityFactory(conditionsObj)
      case this._bmwBank:
        return this._bmwBankConditionEntityFactory(conditionsObj)
      case this._caixa:
        return this._caixaConditionEntityFactory(conditionsObj)
      case this._cofidis:
        return this._cofidisConditionEntityFactory(conditionsObj)
      case this._lendrock:
        return this._lendrockConditionEntityFactory(conditionsObj)
      case this._mobilize:
      case this._mobilizeNissan:
        return this._mobilizeConditionEntityFactory(conditionsObj)
      case this._santander:
        return this._santanderConditionEntityFactory(conditionsObj)
      case this._santanderPri:
        return this._santanderPriConditionEntityFactory(conditionsObj)
      case this._stellantis:
        return this._stellantisConditionEntityFactory(conditionsObj)
      case this._vwfs:
        return this._vwfsConditionEntityFactory(conditionsObj)
      case this._confia:
        return this._confiaConditionEntityFactory(conditionsObj)
      default:
        throw this._validationError('Lender not supported')
    }
  }

  isFormatedDate = date => date instanceof Date

  /**
   * @return {Object} financingTermsRawData
   */
  map = ({
    amountToFinance,
    availableTerm,
    availableTerms,
    capital,
    casualtyInsuranceAmount,
    coefficient,
    commissionEur,
    commission,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareId,
    fareInsuranceId,
    fee,
    financedPrice,
    firstFee,
    id,
    insuranceCoverages,
    insuranceId,
    insuranceName,
    insuranceRate,
    isOpeningExpensesFinanced,
    lender,
    lenderLegalTextId,
    licenseWithdrawalInsuranceAmount,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    termsUrl,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount,
    dateOfferEnd
  }) => {
    // log error if firstFee is < 0
    if (firstFee < 0) console.error(`termsUrl: ${termsUrl}, firstFee: ${firstFee}`)

    const conditionsObj = {
      amountToFinance,
      availableTerm,
      availableTerms,
      capital,
      casualtyInsuranceAmount,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment: this.isFormatedDate(dateFirstInstallment)
        ? dateFirstInstallment
        : new Date(dateFirstInstallment).toLocaleDateString(this._culture),
      dateSignup: this.isFormatedDate(dateSignup) ? dateSignup : new Date(dateSignup).toLocaleDateString(this._culture),
      entry,
      fareId,
      fareInsuranceId,
      fee,
      financedPrice,
      firstFee: firstFee || fee,
      id,
      insuranceCoverages,
      insuranceId,
      insuranceName,
      insuranceRate,
      isOpeningExpensesFinanced,
      lender,
      lenderLegalTextId: this.getLenderLegalTextId({fareId, insuranceId, lender}),
      licenseWithdrawalInsuranceAmount,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      termsUrl,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount,
      dateOfferEnd: this.isFormatedDate(dateOfferEnd)
        ? dateOfferEnd
        : new Date(dateOfferEnd).toLocaleDateString(this._culture)
    }
    return this.getLendersConditions({conditionsObj})
  }
}
